import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CommandeService } from '../../services/commande.service';
import { MiseadispoService } from '../../services/miseadispo.service';
import { AuthService } from '../../services/auth.service';
import { ConfirmationService } from '../../services/confirmation.service';
import { UserSessionService } from '../../services/user-session.service';
import { WebSocketService } from '../../services/websocket.service';
import { Commande } from '../../models/commande.model';
import { MiseadispoAuto } from '../../models/miseadispo-auto.model';
import { Miseadispo } from '../../models/miseadispo.model';
import { User } from '../../models/user.model';
import { forkJoin, Subscription, of, Observable } from 'rxjs';
import { switchMap, timeout, catchError, tap, map } from 'rxjs/operators';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit, OnDestroy {
  private readonly ACCORDEON_STATE_KEY = 'dashboard_accordeon_state';

  currentUser: User | null = null;
  commandesDuJour: Commande[] = [];
  miseadisposAuto: MiseadispoAuto[] = [];
  miseadisposDuJour: Miseadispo[] = [];
  stats = {
    totalCommandes: 0,
    commandesRecuperees: 0,
    commandesEnAttente: 0,
    activeUsers: 0
  };
  // Loadings indépendants pour chaque carte
  loadingCommandes = true;
  loadingProduitsAValider = true;
  loadingProduitsDuJour = true;
  isMagasin = false;
  isSuperAdmin = false;
  private activeUsersInterval: any;
  private wsSubscriptions: Subscription[] = [];

  // Groupes de commandes par statut
  commandesParStatut: { [key: string]: Commande[] } = {
    '1': [], // En cours
    '2': [], // Validée
    '3': [], // Récupérée
    '4': [], // Non récupérée
    '5': []  // Annulée
  };

  // Sélection multiple pour les commandes à valider
  selectedCommandeIds: Set<string> = new Set();
  isValidatingAndPrinting: boolean = false;

  // État d'ouverture/fermeture des accordéons (tous ouverts par défaut)
  accordeonOuvert: { [key: string]: boolean } = {
    '1': true,
    '2': true,
    '3': true,
    '4': true,
    '5': true
  };

  constructor(
    private commandeService: CommandeService,
    private miseadispoService: MiseadispoService,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
    private userSessionService: UserSessionService,
    private websocketService: WebSocketService
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.currentUser();
    // isMagasin = true pour magasin OU super_admin (accès complet pour super_admin)
    this.isMagasin = this.currentUser?.profils?.some(p => p.code === 'magasin' || p.code === 'super_admin') ?? false;
    // isSuperAdmin = true uniquement pour super_admin
    this.isSuperAdmin = this.currentUser?.profils?.some(p => p.code === 'super_admin') ?? false;
    this.loadAccordeonState();
    this.loadData();
    this.loadActiveUsers();
    this.startActiveUsersPolling();
    this.connectWebSocket();
  }

  /**
   * Récupère le magasinId d'un utilisateur
   */
  getMagasinId(user: User | null): number | null {
    if (!user) return null;

    let appData = user.app_data;

    // Parser si c'est une string
    if (typeof appData === 'string') {
      try {
        appData = JSON.parse(appData);
      } catch (e) {
        return null;
      }
    }

    return (appData as any)?.labase?.magasinId || null;
  }


  ngOnDestroy(): void {
    this.stopActiveUsersPolling();
    this.disconnectWebSocket();
  }

  /**
   * Connecte au WebSocket et écoute les événements en temps réel
   */
  private connectWebSocket(): void {
    const token = this.authService.getToken();
    if (!token) {
      console.warn('[Dashboard] No token available for WebSocket connection');
      return;
    }

    // Connexion au WebSocket
    this.websocketService.connect(token);

    // Écouter les nouvelles commandes
    const newOrderSub = this.websocketService.newOrder$.subscribe((event) => {
      console.log('[Dashboard] Nouvelle commande reçue:', event);

      // Afficher une notification visuelle
      this.showNotification(`Nouvelle commande #${event.orderNumber}`, 'success');

      // Recharger uniquement les commandes du jour
      this.loadCommandesDuJour();
    });

    // Écouter les changements de statut de commandes
    const statusChangeSub = this.websocketService.orderStatusChange$.subscribe((event) => {
      console.log('[Dashboard] Changement de statut:', event);

      // Ne pas afficher de notification car c'est souvent l'admin qui fait le changement
      // Juste recharger les commandes pour mettre à jour l'affichage
      this.loadCommandesDuJour();
    });

    // Écouter les nouvelles mises à disposition
    const newMiseADispoSub = this.websocketService.newMiseADispo$.subscribe((event) => {
      console.log('[Dashboard] Nouvelle mise à disposition reçue:', event);

      // Afficher une notification visuelle
      this.showNotification(`Nouveau produit à valider: ${event.productName}`, 'warning');

      // Recharger les produits à valider (appelle n8n puis backend)
      this.loadProduitsAValider();

      // Recharger aussi les produits mis à disposition aujourd'hui
      this.loadProduitsDuJour();
    });

    // Stocker les subscriptions pour les nettoyer plus tard
    this.wsSubscriptions.push(newOrderSub, statusChangeSub, newMiseADispoSub);
  }

  /**
   * Déconnecte du WebSocket et nettoie les subscriptions
   */
  private disconnectWebSocket(): void {
    // Nettoyer les subscriptions
    this.wsSubscriptions.forEach(sub => sub.unsubscribe());
    this.wsSubscriptions = [];

    // Déconnecter du WebSocket
    this.websocketService.disconnect();
  }

  /**
   * Affiche une notification toast en haut à droite
   */
  private showNotification(message: string, type: 'success' | 'info' | 'warning' | 'error' = 'info'): void {
    const notification = document.createElement('div');

    const colors = {
      success: '#4caf50',
      info: '#2196f3',
      warning: '#ff9800',
      error: '#f44336'
    };

    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${colors[type]};
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
      animation: slideInRight 0.3s ease-out;
      max-width: 350px;
    `;

    notification.textContent = message;
    document.body.appendChild(notification);

    // Retirer après 4 secondes
    setTimeout(() => {
      notification.style.animation = 'slideOutRight 0.3s ease-out';
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 4000);
  }

  /**
   * Charge l'état des accordéons depuis le localStorage
   */
  private loadAccordeonState(): void {
    try {
      const savedState = localStorage.getItem(this.ACCORDEON_STATE_KEY);
      if (savedState) {
        this.accordeonOuvert = JSON.parse(savedState);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'état des accordéons:', error);
    }
  }

  /**
   * Sauvegarde l'état des accordéons dans le localStorage
   */
  private saveAccordeonState(): void {
    try {
      localStorage.setItem(this.ACCORDEON_STATE_KEY, JSON.stringify(this.accordeonOuvert));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'état des accordéons:', error);
    }
  }

  /**
   * Charge toutes les données du dashboard (commandes + produits)
   * Utilisé uniquement au chargement initial du composant
   */
  loadData(): void {
    // Activer tous les loadings
    this.loadingCommandes = true;
    if (this.isMagasin) {
      this.loadingProduitsAValider = true;
      this.loadingProduitsDuJour = true;
    }

    if (this.isMagasin) {
      // Charger commandes, produits à valider et produits du jour en parallèle (chacun gère son propre loading)
      // Commandes
      this.loadCommandesDuJourData().subscribe({
        next: () => {
          this.loadStats();
          this.loadingCommandes = false;
        },
        error: (error) => {
          console.error('Erreur chargement commandes:', error);
          this.loadingCommandes = false;
        }
      });

      // Produits à valider
      this.loadProduitsAValiderData().subscribe({
        next: () => {
          this.loadingProduitsAValider = false;
        },
        error: (error) => {
          console.error('Erreur chargement produits à valider:', error);
          this.loadingProduitsAValider = false;
        }
      });

      // Produits du jour
      this.loadProduitsDuJourData().subscribe({
        next: () => {
          this.loadingProduitsDuJour = false;
        },
        error: (error) => {
          console.error('Erreur chargement produits du jour:', error);
          this.loadingProduitsDuJour = false;
        }
      });
    } else {
      // Super admin: charger uniquement les commandes
      this.loadCommandesDuJourData().subscribe({
        next: () => {
          this.loadStats();
          this.loadingCommandes = false;
        },
        error: (error) => {
          console.error('Erreur chargement commandes:', error);
          this.loadingCommandes = false;
        }
      });
    }
  }

  /**
   * Reload uniquement les commandes du jour
   */
  loadCommandesDuJour(): void {
    this.loadingCommandes = true;
    this.loadCommandesDuJourData().subscribe({
      next: () => {
        this.loadStats();
        this.loadingCommandes = false;
      },
      error: (error) => {
        console.error('Erreur reload commandes:', error);
        this.loadingCommandes = false;
      }
    });
  }

  /**
   * Méthode interne pour charger les commandes du jour
   */
  private loadCommandesDuJourData(): Observable<void> {
    const today = new Date().toISOString().split('T')[0];

    return this.commandeService.getAllCommandes().pipe(
      map(commandes => {
        // Filtrer les commandes du jour
        this.commandesDuJour = commandes.filter(cmd => {
          const cmdDate = new Date(cmd.createdAt || '').toISOString().split('T')[0];
          return cmdDate === today;
        });

        // Regrouper les commandes par statut
        this.regrouperCommandesParStatut();
      })
    );
  }

  /**
   * Reload les produits à valider: appelle d'abord n8n puis charge les produits
   */
  loadProduitsAValider(): void {
    if (!this.isMagasin) return;

    this.loadingProduitsAValider = true;
    this.loadProduitsAValiderData().subscribe({
      next: () => {
        this.loadingProduitsAValider = false;
      },
      error: (error) => {
        console.error('Erreur reload produits à valider:', error);
        this.loadingProduitsAValider = false;
      }
    });
  }

  /**
   * Méthode interne pour charger les produits à valider
   * Appelle d'abord n8n puis charge TOUJOURS les miseadisposAuto (statusdispo = 2) depuis la BDD
   */
  private loadProduitsAValiderData(): Observable<void> {
    // 1. Appeler n8n webhook (avec gestion complète des erreurs)
    return this.miseadispoService.triggerN8nWebhook().pipe(
      timeout(10000), // 10 second timeout
      catchError(error => {
        console.warn('Webhook n8n failed or timed out, continuing anyway:', error);
        return of({ success: false }); // Continue quoi qu'il arrive
      }),
      tap(result => {
        if (result && result.success) {
          console.log('Webhook n8n triggered successfully');
        }
      }),
      // 2. Dans TOUS les cas (succès ou échec n8n), charger les produits depuis la BDD
      switchMap(() => this.miseadispoService.getMyMiseadisposAuto().pipe(
        catchError(error => {
          console.error('Erreur chargement produits BDD:', error);
          return of([]); // Retourner tableau vide en cas d'erreur BDD
        })
      )),
      map(miseadisposAuto => {
        this.miseadisposAuto = miseadisposAuto;
      })
    );
  }

  /**
   * Reload uniquement les produits mis à disposition aujourd'hui
   */
  loadProduitsDuJour(): void {
    if (!this.isMagasin) return;

    this.loadingProduitsDuJour = true;
    this.loadProduitsDuJourData().subscribe({
      next: () => {
        this.loadingProduitsDuJour = false;
      },
      error: (error) => {
        console.error('Erreur reload produits du jour:', error);
        this.loadingProduitsDuJour = false;
      }
    });
  }

  /**
   * Méthode interne pour charger les produits du jour
   */
  private loadProduitsDuJourData(): Observable<void> {
    const today = new Date().toISOString().split('T')[0];

    return this.miseadispoService.getMiseadispos().pipe(
      map(miseadispos => {
        // Filtrer les mises à disposition d'aujourd'hui
        this.miseadisposDuJour = miseadispos.filter(miseadispo => {
          const miseadispoDate = new Date(miseadispo.date).toISOString().split('T')[0];
          return miseadispoDate === today;
        });
      })
    );
  }

  /**
   * Recalcule les statistiques basées sur les données actuelles
   */
  loadStats(): void {
    this.stats.totalCommandes = this.commandesDuJour.length;
    this.stats.commandesRecuperees = this.commandesDuJour.filter(cmd => cmd.statutOrdre === 3).length;
    this.stats.commandesEnAttente = this.commandesDuJour.filter(cmd => cmd.statutOrdre === 1).length;
  }

  regrouperCommandesParStatut(): void {
    // Réinitialiser les groupes
    this.commandesParStatut = {
      '1': [], // En cours
      '2': [], // Validée
      '3': [], // Récupérée
      '4': [], // Non récupérée
      '5': []  // Annulée
    };

    // Regrouper les commandes par statut (utiliser statutOrdre)
    this.commandesDuJour.forEach(commande => {
      const statutKey = String(commande.statutOrdre);
      if (this.commandesParStatut[statutKey] !== undefined) {
        this.commandesParStatut[statutKey].push(commande);
      }
    });
  }

  toggleAccordeon(statutId: number): void {
    this.accordeonOuvert[String(statutId)] = !this.accordeonOuvert[String(statutId)];
    this.saveAccordeonState();
  }

  getStatutCount(statutId: number): number {
    return this.commandesParStatut[String(statutId)]?.length || 0;
  }

  getStatutLabel(statutId: number): string {
    const statuts: { [key: number]: string } = {
      1: 'En cours',
      2: 'Validée',
      3: 'Récupérée',
      4: 'Non récupérée',
      5: 'Annulée'
    };
    return statuts[statutId] || 'Inconnu';
  }

  async onStatusChange(commande: Commande, newStatutOrdre?: number): Promise<void> {
    if (!commande.id) return;

    const statutOrdre = newStatutOrdre || commande.statutOrdre;
    if (!statutOrdre) return;

    // Si le statut est "Annulée" (ordre 5), demander le motif
    let motifAnnulation: string | undefined = undefined;
    if (statutOrdre === 5) {
      motifAnnulation = await this.demanderMotifAnnulation(commande.NumCom);
      if (!motifAnnulation) {
        // L'utilisateur a annulé, restaurer le statut précédent
        this.loadCommandesDuJour();
        return;
      }
    }

    this.commandeService.updateCommandeStatus(commande.id, statutOrdre, motifAnnulation).subscribe({
      next: () => {
        // Recharger les commandes pour avoir les informations à jour
        this.loadCommandesDuJour();
      },
      error: (error) => {
        console.error('Erreur mise à jour statut:', error);
        // Recharger les commandes en cas d'erreur
        this.loadCommandesDuJour();
      }
    });
  }

  // Passer une commande à l'étape suivante
  passerEtapeSuivante(commande: Commande): void {
    if (!commande.id) return;

    // Déterminer le prochain statut selon le flux logique
    let prochainStatutOrdre: number | null = null;
    switch (commande.statutOrdre) {
      case 1: // En cours → Validée
        prochainStatutOrdre = 2;
        break;
      case 2: // Validée → Récupérée
        prochainStatutOrdre = 3;
        break;
      default:
        return; // Pas de transition pour les autres statuts
    }

    if (prochainStatutOrdre) {
      commande.statutOrdre = prochainStatutOrdre;
      this.onStatusChange(commande, prochainStatutOrdre);
    }
  }

  // Passer toutes les commandes d'un groupe à l'étape suivante
  async passerToutesEtapeSuivante(statutActuel: number): Promise<void> {
    const commandes = this.commandesParStatut[String(statutActuel)];
    if (!commandes || commandes.length === 0) return;

    // Déterminer le prochain statut selon le flux logique
    let prochainStatut: number | null = null;
    switch (statutActuel) {
      case 1: // En cours → Validée
        prochainStatut = 2;
        break;
      case 2: // Validée → Récupérée
        prochainStatut = 3;
        break;
      default:
        return; // Pas de transition pour les autres statuts
    }

    if (!prochainStatut) return;

    const statutLabel = this.getStatutLabel(statutActuel);
    const prochainStatutLabel = this.getStatutLabel(prochainStatut);

    const confirmed = await this.confirmationService.confirm({
      title: 'Passer toutes les commandes',
      message: `Voulez-vous passer toutes les commandes "${statutLabel}" vers "${prochainStatutLabel}" ? (${commandes.length} commande(s))`,
      confirmText: 'Confirmer',
      cancelText: 'Annuler'
    });

    if (confirmed) {
      // Mettre à jour toutes les commandes
      commandes.forEach(commande => {
        if (commande.id) {
          commande.statutOrdre = prochainStatut!;
          this.commandeService.updateCommandeStatus(commande.id, prochainStatut!).subscribe({
            error: (error) => {
              console.error(`Erreur mise à jour commande ${commande.NumCom}:`, error);
            }
          });
        }
      });

      // Attendre un peu puis recharger les commandes
      setTimeout(() => {
        this.loadCommandesDuJour();
      }, 1000);
    }
  }

  /**
   * Affiche un dialog pour sélectionner un motif d'annulation
   * @param numCommande Le numéro de commande à annuler
   * @returns Le motif choisi ou undefined si annulé
   */
  private async demanderMotifAnnulation(numCommande: string): Promise<string | undefined> {
    // Liste des motifs d'annulation prédéfinis
    const motifs = [
      'Produit non disponible',
      'Produit abîmé ou impropre à la consommation',
      'Erreur dans la commande',
      'Demande de l\'étudiant',
      'Stock insuffisant',
      'Autre raison'
    ];

    // Créer un prompt pour sélectionner le motif
    return new Promise<string | undefined>((resolve) => {
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 10000; display: flex; align-items: center; justify-content: center;';

      const dialog = document.createElement('div');
      dialog.style.cssText = 'background: white; border-radius: 12px; padding: 30px; max-width: 500px; width: 90%; box-shadow: 0 10px 40px rgba(0,0,0,0.3);';

      dialog.innerHTML = `
        <h2 style="margin: 0 0 20px 0; font-size: 20px; color: #333;">Motif d'annulation</h2>
        <p style="margin: 0 0 20px 0; color: #666;">Sélectionnez le motif d'annulation de la commande ${numCommande}</p>
        <div style="margin-bottom: 20px;">
          ${motifs.map((motif, index) => `
            <label style="display: block; padding: 12px; margin-bottom: 8px; border: 2px solid #e0e0e0; border-radius: 8px; cursor: pointer; transition: all 0.2s;">
              <input type="radio" name="motif" value="${motif}" style="margin-right: 10px;">
              <span style="color: #333;">${motif}</span>
            </label>
          `).join('')}
        </div>
        <div style="display: flex; gap: 10px; justify-content: flex-end;">
          <button id="cancel-btn" style="padding: 10px 20px; border: none; background: #f0f0f0; border-radius: 6px; cursor: pointer; font-weight: 600; color: #666;">Annuler</button>
          <button id="confirm-btn" style="padding: 10px 20px; border: none; background: #de0f17; color: white; border-radius: 6px; cursor: pointer; font-weight: 600;" disabled>Confirmer</button>
        </div>
      `;

      overlay.appendChild(dialog);
      document.body.appendChild(overlay);

      // Gérer la sélection des radios
      const radios = dialog.querySelectorAll('input[type="radio"]');
      const confirmBtn = dialog.querySelector('#confirm-btn') as HTMLButtonElement;
      const labels = dialog.querySelectorAll('label');

      radios.forEach((radio, index) => {
        radio.addEventListener('change', () => {
          confirmBtn.disabled = false;
          // Mettre à jour les styles des labels
          labels.forEach((label, labelIndex) => {
            if (labelIndex === index) {
              label.style.borderColor = '#de0f17';
              label.style.background = '#fff5f5';
            } else {
              label.style.borderColor = '#e0e0e0';
              label.style.background = 'transparent';
            }
          });
        });
      });

      dialog.querySelector('#confirm-btn')?.addEventListener('click', () => {
        const selected = dialog.querySelector('input[name="motif"]:checked') as HTMLInputElement;
        document.body.removeChild(overlay);
        resolve(selected ? selected.value : undefined);
      });

      dialog.querySelector('#cancel-btn')?.addEventListener('click', () => {
        document.body.removeChild(overlay);
        resolve(undefined);
      });

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          document.body.removeChild(overlay);
          resolve(undefined);
        }
      });
    });
  }

  // Annuler une commande
  async annulerCommande(commande: Commande): Promise<void> {
    if (!commande.id) return;

    const motifChoisi = await this.demanderMotifAnnulation(commande.NumCom);
    if (!motifChoisi) return;

    // Ordre 5 pour "Annulée" selon le mapping frontend
    this.commandeService.updateCommandeStatus(commande.id, 5, motifChoisi).subscribe({
      next: () => {
        this.loadCommandesDuJour();
      },
      error: (error) => {
        console.error(`Erreur annulation commande ${commande.NumCom}:`, error);
        this.loadCommandesDuJour();
      }
    });
  }

  // Obtenir le label du bouton d'action selon le statut
  getActionButtonLabel(statutId: number): string {
    const labels: { [key: number]: string } = {
      1: 'Valider',
      2: 'Marquer récupérée'
    };
    return labels[statutId] || '';
  }

  // Obtenir l'icône du bouton d'action selon le statut
  getActionButtonIcon(statutId: number): string {
    const icons: { [key: number]: string } = {
      1: 'pi-check',
      2: 'pi-check-circle'
    };
    return icons[statutId] || 'pi-arrow-right';
  }

  // Vérifier si un statut peut passer à l'étape suivante
  peutPasserEtapeSuivante(statutId: number): boolean {
    return statutId === 1 || statutId === 2;
  }

  // Obtenir le prochain statut selon le statut actuel
  getProchainStatut(statutId: number): number {
    switch (statutId) {
      case 1: return 2; // En cours → Validée
      case 2: return 3; // Validée → Récupérée
      default: return statutId;
    }
  }

  async validateMiseadispo(miseadispo: MiseadispoAuto): Promise<void> {
    if (!miseadispo.id) return;

    const confirmed = await this.confirmationService.confirm({
      title: 'Validation du produit',
      message: `Voulez-vous valider le produit "${miseadispo.libelle}" ?`,
      confirmText: 'Valider',
      cancelText: 'Annuler'
    });

    if (confirmed) {
      // Utiliser la quantité modifiée si elle existe
      const quantite = miseadispo.quantiteActuel > 0 ? miseadispo.quantiteActuel : undefined;
      this.miseadispoService.validateMiseadispoAuto(miseadispo.id, quantite).subscribe({
        next: () => {
          // Retirer le produit de la liste
          this.miseadisposAuto = this.miseadisposAuto.filter(m => m.id !== miseadispo.id);

          // Recharger les produits du jour car le produit validé doit y apparaître
          this.loadProduitsDuJour();
        },
        error: (error) => {
          console.error('Erreur validation produit:', error);
        }
      });
    }
  }

  async validateAll(): Promise<void> {
    const count = this.miseadisposAuto.length;
    if (count === 0) return;

    const message = count === 1
      ? 'Voulez-vous valider ce produit ?'
      : `Voulez-vous valider tous les ${count} produits ?`;

    const confirmed = await this.confirmationService.confirm({
      title: 'Validation groupée',
      message: message,
      confirmText: 'Tout valider',
      cancelText: 'Annuler'
    });

    if (confirmed) {
      this.miseadispoService.validateAllMiseadisposAuto().subscribe({
        next: () => {
          // Vider la liste
          this.miseadisposAuto = [];

          // Recharger les produits du jour car tous les produits validés doivent y apparaître
          this.loadProduitsDuJour();
        },
        error: (error) => {
          console.error('Erreur validation des produits:', error);
        }
      });
    }
  }

  /**
   * Supprime une mise à disposition auto (produit à valider)
   */
  async deleteMiseadispo(miseadispo: MiseadispoAuto): Promise<void> {
    if (!miseadispo.id) return;

    const confirmed = await this.confirmationService.confirm({
      title: 'Supprimer le produit',
      message: `Voulez-vous vraiment supprimer "${miseadispo.libelle}" de la liste des produits à valider ?`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler'
    });

    if (confirmed) {
      this.miseadispoService.deleteMiseadispo(miseadispo.id).subscribe({
        next: () => {
          // Retirer le produit de la liste
          this.miseadisposAuto = this.miseadisposAuto.filter(m => m.id !== miseadispo.id);
        },
        error: async (error) => {
          console.error('Erreur suppression produit:', error);
          await this.confirmationService.confirm({
            title: 'Erreur',
            message: 'Une erreur est survenue lors de la suppression du produit.',
            confirmText: 'OK',
            cancelText: ''
          });
        }
      });
    }
  }

  /**
   * Charge le nombre d'utilisateurs actifs
   */
  loadActiveUsers(): void {
    this.userSessionService.getActiveUsersCount().subscribe({
      next: (response) => {
        this.stats.activeUsers = response.activeCount;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des utilisateurs actifs:', error);
        this.stats.activeUsers = 0;
      }
    });
  }

  /**
   * Démarre le polling pour rafraîchir le nombre d'utilisateurs actifs toutes les 30 secondes
   */
  private startActiveUsersPolling(): void {
    this.activeUsersInterval = setInterval(() => {
      this.loadActiveUsers();
    }, 30000); // 30 secondes
  }

  /**
   * Arrête le polling des utilisateurs actifs
   */
  private stopActiveUsersPolling(): void {
    if (this.activeUsersInterval) {
      clearInterval(this.activeUsersInterval);
      this.activeUsersInterval = null;
    }
  }

  /**
   * Réimprime le ticket d'une commande
   */
  async reprintCommande(commande: Commande): Promise<void> {
    if (!commande.id) return;

    this.commandeService.printCommande(commande.id).subscribe({
      next: () => {
        this.showNotification(`Ticket ${commande.NumCom} envoyé à l'impression`, 'success');
      },
      error: (error) => {
        console.error('Erreur impression:', error);
        this.showNotification(`Erreur impression ${commande.NumCom}`, 'error');
      }
    });
  }

  /**
   * Renvoie la notification de mise à disposition aux étudiants
   */
  isSendingNotification = false;

  async resendNotification(): Promise<void> {
    const confirmed = await this.confirmationService.confirm({
      title: 'Renvoyer la notification',
      message: 'Voulez-vous renvoyer la notification de disponibilité des produits aux étudiants ?',
      confirmText: 'Envoyer',
      cancelText: 'Annuler'
    });

    if (!confirmed) return;

    this.isSendingNotification = true;

    this.miseadispoService.resendNotification().subscribe({
      next: (result) => {
        this.isSendingNotification = false;
        if (result.success) {
          this.showNotification(result.message, 'success');
        } else {
          this.showNotification(result.message, 'warning');
        }
      },
      error: (error) => {
        this.isSendingNotification = false;
        console.error('Erreur envoi notification:', error);
        this.showNotification('Erreur lors de l\'envoi de la notification', 'error');
      }
    });
  }

  /**
   * Annule une mise à disposition (la rend "Pas Disponible")
   */
  async cancelMiseadispo(miseadispo: Miseadispo): Promise<void> {
    if (!miseadispo.id) return;

    const confirmed = await this.confirmationService.confirm({
      title: 'Annuler la mise à disposition',
      message: `Voulez-vous annuler le produit "${miseadispo.libelle}" ? Il ne sera plus visible par les étudiants.`,
      confirmText: 'Annuler la mise à disposition',
      cancelText: 'Conserver'
    });

    if (confirmed) {
      this.miseadispoService.cancelMiseadispo(miseadispo.id).subscribe({
        next: () => {
          // Retirer le produit de la liste des produits du jour
          this.miseadisposDuJour = this.miseadisposDuJour.filter(m => m.id !== miseadispo.id);

          // Recharger les produits à valider car le produit annulé doit y réapparaître
          this.loadProduitsAValider();
        },
        error: (error) => {
          console.error('Erreur annulation produit:', error);
        }
      });
    }
  }

  /**
   * Copie du texte dans le presse-papiers avec feedback visuel discret
   */
  async copyToClipboard(text: string, label: string, event?: Event): Promise<void> {
    // IMPORTANT : Capturer la référence AVANT l'appel async
    // car currentTarget devient null après l'événement
    const target = event?.currentTarget as HTMLElement;

    try {
      await navigator.clipboard.writeText(text);

      // Feedback visuel sur l'élément cliqué
      if (target && target.classList) {
        target.classList.add('copied');

        setTimeout(() => {
          if (target && target.classList) {
            target.classList.remove('copied');
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
    }
  }

  /**
   * Gestion de la sélection multiple pour validation et impression
   */
  toggleCommandeSelection(commandeId: string): void {
    if (this.selectedCommandeIds.has(commandeId)) {
      this.selectedCommandeIds.delete(commandeId);
    } else {
      this.selectedCommandeIds.add(commandeId);
    }
  }

  isCommandeSelected(commandeId: string): boolean {
    return this.selectedCommandeIds.has(commandeId);
  }

  toggleSelectAllEnCours(): void {
    const commandesEnCours = this.commandesParStatut['1'];
    if (!commandesEnCours) return;

    const allSelected = commandesEnCours.every(cmd => this.selectedCommandeIds.has(cmd.id!));

    if (allSelected) {
      // Déselectionner toutes
      commandesEnCours.forEach(cmd => {
        if (cmd.id) this.selectedCommandeIds.delete(cmd.id);
      });
    } else {
      // Sélectionner toutes
      commandesEnCours.forEach(cmd => {
        if (cmd.id) this.selectedCommandeIds.add(cmd.id);
      });
    }
  }

  areAllEnCoursSelected(): boolean {
    const commandesEnCours = this.commandesParStatut['1'];
    if (!commandesEnCours || commandesEnCours.length === 0) return false;
    return commandesEnCours.every(cmd => this.selectedCommandeIds.has(cmd.id!));
  }

  /**
   * Valide les commandes sélectionnées et appelle le webhook d'impression
   */
  async validerEtImprimerSelection(): Promise<void> {
    if (this.selectedCommandeIds.size === 0) return;

    const confirmed = await this.confirmationService.confirm({
      title: 'Valider et imprimer',
      message: `Voulez-vous valider et imprimer ${this.selectedCommandeIds.size} commande(s) ?`,
      confirmText: 'Valider et imprimer',
      cancelText: 'Annuler'
    });

    if (!confirmed) return;

    this.isValidatingAndPrinting = true;

    try {
      // Récupérer les commandes sélectionnées
      const commandesAValider = Array.from(this.selectedCommandeIds)
        .map(id => this.commandesDuJour.find(cmd => cmd.id === id))
        .filter(cmd => cmd !== undefined) as Commande[];

      // Valider et imprimer chaque commande
      for (const commande of commandesAValider) {
        try {
          // Mettre à jour le statut vers "Validée" (ordre 2)
          await this.commandeService.updateCommandeStatus(commande.id!, 2).toPromise();

          // Appeler le webhook d'impression
          await this.commandeService.printCommande(commande.id!).toPromise();

          console.log(`Commande ${commande.NumCom} validée et envoyée à l'impression`);
        } catch (error) {
          console.error(`Erreur lors de la validation/impression de la commande ${commande.NumCom}:`, error);
          this.showNotification(`Erreur pour la commande ${commande.NumCom}`, 'error');
        }
      }

      // Vider la sélection
      this.selectedCommandeIds.clear();

      // Recharger les commandes
      this.loadCommandesDuJour();

      this.showNotification(`${commandesAValider.length} commande(s) validée(s) et envoyée(s) à l'impression`, 'success');
    } catch (error) {
      console.error('Erreur lors de la validation/impression:', error);
      await this.confirmationService.confirm({
        title: 'Erreur',
        message: 'Une erreur est survenue lors de la validation et de l\'impression des commandes.',
        confirmText: 'OK',
        cancelText: ''
      });
    } finally {
      this.isValidatingAndPrinting = false;
    }
  }
}
